import {
  FormEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  approveOfficialDocument,
  createOfficialDocument,
  deleteOfficialDocument,
  deleteOfficialDocumentAttachment,
  downloadOfficialDocumentAttachment,
  fetchOfficialDocument,
  fetchOfficialDocuments,
  revokeOfficialDocument,
  updateOfficialDocument,
  uploadOfficialDocumentAttachments,
  downloadOfficialDocumentPdf,
} from "../../api/officialDocument";
import { useCompanyStore } from "../../store/companyStore";
import { APPROVAL_TARGET_OFFICIAL_DOCUMENT } from "../../types/approval";
import { canConfirmInspection } from "../../types/companyRole";
import {
  OfficialDocument,
  OfficialDocumentAttachment,
  OfficialDocumentDirection,
} from "../../types/officialDocument";
import { notifyError, reportError } from "../../utils/notify";
import ApprovalCancelButton from "../approval/ApprovalCancelButton";
import ApprovalRequestModal from "../approval/ApprovalRequestModal";

import {
  withApprovalPendingClass,
  withApprovalPendingLabel,
} from "../../utils/approvalPending";
import "../quality/EducationAnnualPlanTab.css";
import "../quality/QualityShell.css";
import "../product/InspectionFormSheet.css";
import "../product/NonconformanceReportPanel.css";

function formatDate(value?: string | null) {
  if (!value) return "-";
  return value.slice(0, 10);
}

function formatSize(size?: number | null) {
  if (size == null) return "";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

function apiErrorMessage(err: unknown, fallback: string) {
  const message = (err as { response?: { data?: { message?: string } } })
    ?.response?.data?.message;
  return message?.trim() || fallback;
}

function isApproved(status?: string | null) {
  return (status || "").toUpperCase() === "APPROVED";
}

function isDraft(status?: string | null) {
  const s = (status || "DRAFT").toUpperCase();
  return s === "DRAFT" || s === "";
}

function directionLabel(direction?: string | null) {
  if (direction === "INCOMING") return "수신";
  if (direction === "OUTGOING") return "발신";
  return direction || "-";
}

function StampBox({
  authorName,
  reviewerName,
  approverName,
}: {
  authorName?: string | null;
  reviewerName?: string | null;
  approverName?: string | null;
}) {
  return (
    <div className="ifs-sheet__stamps">
      {(
        [
          ["작성", authorName],
          ["검토", reviewerName],
          ["승인", approverName],
        ] as const
      ).map(([label, value]) => (
        <div key={label}>
          <strong>{label}</strong>
          <span
            style={{
              color: value ? "#1f2a37" : "#98a2b3",
              fontWeight: value ? 600 : 400,
            }}
          >
            {value || "-"}
          </span>
        </div>
      ))}
    </div>
  );
}

type DocView =
  | { type: "list" }
  | { type: "create" }
  | { type: "edit"; officialDocumentId: number }
  | { type: "detail"; officialDocumentId: number };

const LIST_VIEW = { type: "list" } as const;

type DirectionFilter = "" | OfficialDocumentDirection;

function OfficialDocumentPanel({
  contractId = null,
  embedded = false,
}: {
  contractId?: number | null;
  embedded?: boolean;
} = {}) {
  void embedded;
  const companyId = useCompanyStore((s) => s.companyId);
  const role = useCompanyStore((s) => s.role);
  const canApproveDirect = canConfirmInspection(role);
  const fileRef = useRef<HTMLInputElement>(null);

  const [view, setView] = useState<DocView>(LIST_VIEW);
  const [rows, setRows] = useState<OfficialDocument[]>([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [directionFilter, setDirectionFilter] = useState<DirectionFilter>("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [error, setError] = useState("");
  const [approvalMessage, setApprovalMessage] = useState("");
  const [approvalOpen, setApprovalOpen] = useState(false);

  const [documentNo, setDocumentNo] = useState("");
  const [direction, setDirection] =
    useState<OfficialDocumentDirection>("OUTGOING");
  const [title, setTitle] = useState("");
  const [counterpart, setCounterpart] = useState("");
  const [documentDate, setDocumentDate] = useState("");
  const [content, setContent] = useState("");
  const [remark, setRemark] = useState("");
  const [status, setStatus] = useState("DRAFT");
  const [authorName, setAuthorName] = useState<string | null>(null);
  const [reviewerName, setReviewerName] = useState<string | null>(null);
  const [approverName, setApproverName] = useState<string | null>(null);
  const [canRevoke, setCanRevoke] = useState(false);
  const [approvalInProgress, setApprovalInProgress] = useState(false);
  const [pendingApprovalRequestId, setPendingApprovalRequestId] = useState<
    number | null
  >(null);
  const [canCancelApproval, setCanCancelApproval] = useState(false);
  const [attachments, setAttachments] = useState<OfficialDocumentAttachment[]>(
    [],
  );
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);

  const editId =
    view.type === "edit" || view.type === "detail"
      ? view.officialDocumentId
      : null;
  const showForm = view.type !== "list";
  const isDetail = view.type === "detail";
  const isEditing = view.type === "create" || view.type === "edit";
  const locked = !isEditing;

  const attachmentsLocked =
    isApproved(status) ||
    approvalInProgress ||
    (!isEditing && !isDraft(status));

  const applyDetail = (detail: OfficialDocument) => {
    setDocumentNo(detail.documentNo || "");
    setDirection(
      detail.direction === "INCOMING" || detail.direction === "OUTGOING"
        ? detail.direction
        : "OUTGOING",
    );
    setTitle(detail.title || "");
    setCounterpart(detail.counterpart || "");
    setDocumentDate(
      detail.documentDate ? detail.documentDate.slice(0, 10) : "",
    );
    setContent(detail.content || "");
    setRemark(detail.remark || "");
    setStatus(detail.status || "DRAFT");
    setAuthorName(detail.authorName ?? null);
    setReviewerName(detail.reviewerName ?? null);
    setApproverName(detail.approverName ?? null);
    setCanRevoke(detail.canRevoke ?? false);
    setApprovalInProgress(Boolean(detail.approvalInProgress));
    setPendingApprovalRequestId(detail.pendingApprovalRequestId ?? null);
    setCanCancelApproval(Boolean(detail.canCancelApproval));
    setAttachments(detail.attachments || []);
  };

  const loadList = useCallback(
    async (keyword?: string, dir?: DirectionFilter) => {
      try {
        setLoading(true);
        setRows(await fetchOfficialDocuments(keyword, dir || null, contractId));
        setError("");
      } catch (err: unknown) {
        reportError(
          setError,
          apiErrorMessage(err, "공문서 목록을 불러오지 못했습니다."),
        );
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [contractId],
  );

  useEffect(() => {
    setView(LIST_VIEW);
    setSearchKeyword("");
    setDirectionFilter("");
    void loadList();
  }, [companyId, loadList, setView]);

  useEffect(() => {
    if (showForm) return;
    const timer = window.setTimeout(
      () => void loadList(searchKeyword, directionFilter),
      250,
    );
    return () => window.clearTimeout(timer);
  }, [searchKeyword, directionFilter, loadList, showForm]);

  const resetForm = () => {
    setDocumentNo("");
    setDirection("OUTGOING");
    setTitle("");
    setCounterpart("");
    setDocumentDate("");
    setContent("");
    setRemark("");
    setStatus("DRAFT");
    setAuthorName(null);
    setReviewerName(null);
    setApproverName(null);
    setCanRevoke(false);
    setApprovalInProgress(false);
    setPendingApprovalRequestId(null);
    setCanCancelApproval(false);
    setAttachments([]);
    setPendingFiles([]);
    setApprovalMessage("");
    setApprovalOpen(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const openCreate = () => {
    resetForm();
    setView({ type: "create" });
  };

  const openDetail = async (row: OfficialDocument) => {
    try {
      setLoading(true);
      setError("");
      const detail = await fetchOfficialDocument(row.officialDocumentId);
      applyDetail(detail);
      setPendingFiles([]);
      if (fileRef.current) fileRef.current.value = "";
      setView({ type: "detail", officialDocumentId: row.officialDocumentId });
    } catch (err: unknown) {
      reportError(
        setError,
        apiErrorMessage(err, "공문서를 불러오지 못했습니다."),
      );
    } finally {
      setLoading(false);
    }
  };

  const openEditFromDetail = () => {
    if (editId == null || isApproved(status) || approvalInProgress) return;
    setView({ type: "edit", officialDocumentId: editId });
  };

  const backToList = () => {
    resetForm();
    setView(LIST_VIEW);
    void loadList(searchKeyword, directionFilter);
  };

  const handleSave = async (event: FormEvent) => {
    event.preventDefault();
    if (locked) {
      reportError(setError, "승인된 공문서는 수정할 수 없습니다.");
      return;
    }
    if (!title.trim()) {
      reportError(setError, "제목을 입력해 주세요.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      const payload = {
        direction,
        title: title.trim(),
        counterpart: counterpart.trim() || null,
        documentDate: documentDate.trim() || null,
        content: content.trim() || null,
        remark: remark.trim() || null,
        contractId: contractId ?? undefined,
      };
      let officialDocumentId = editId;
      let saved: OfficialDocument;
      if (officialDocumentId != null) {
        saved = await updateOfficialDocument(officialDocumentId, payload);
      } else {
        saved = await createOfficialDocument(payload);
        officialDocumentId = saved.officialDocumentId;
      }
      if (pendingFiles.length > 0 && officialDocumentId != null) {
        const uploaded = await uploadOfficialDocumentAttachments(
          officialDocumentId,
          pendingFiles,
        );
        saved = {
          ...saved,
          attachments: [...(saved.attachments || []), ...uploaded],
          attachmentCount: (saved.attachmentCount ?? 0) + uploaded.length,
        };
        setPendingFiles([]);
      }
      applyDetail(saved);
      setPendingFiles([]);
      setView({ type: "detail", officialDocumentId: saved.officialDocumentId });
      await loadList(searchKeyword, directionFilter);
    } catch (err: unknown) {
      reportError(
        setError,
        apiErrorMessage(err, "공문서 저장에 실패했습니다."),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFromList = async (
    row: OfficialDocument,
    event: MouseEvent,
  ) => {
    event.stopPropagation();
    if (isApproved(row.status)) {
      reportError(setError, "승인된 공문서는 삭제할 수 없습니다.");
      return;
    }
    if (row.approvalInProgress) {
      reportError(
        setError,
        "결재 요청 중인 문서는 삭제할 수 없습니다. 먼저 결재 요청을 취소하세요.",
      );
      return;
    }
    if (!window.confirm(`"${row.title}"를 삭제할까요?`)) return;
    try {
      await deleteOfficialDocument(row.officialDocumentId);
      await loadList(searchKeyword, directionFilter);
    } catch (err: unknown) {
      reportError(
        setError,
        apiErrorMessage(err, "공문서 삭제에 실패했습니다."),
      );
    }
  };

  const handleApprove = async () => {
    if (editId == null) return;
    if (!canApproveDirect) {
      reportError(setError, "바로 승인은 오너/팀장만 가능합니다.");
      return;
    }
    if (
      !window.confirm(
        "공문서를 바로 승인할까요? 승인 후에는 수정·삭제할 수 없습니다.",
      )
    ) {
      return;
    }
    try {
      setApproving(true);
      setError("");
      const saved = await approveOfficialDocument(editId);
      applyDetail(saved);
      setApprovalMessage("바로 승인되었습니다.");
      await loadList(searchKeyword, directionFilter);
    } catch (err: unknown) {
      reportError(setError, apiErrorMessage(err, "바로 승인에 실패했습니다."));
    } finally {
      setApproving(false);
    }
  };

  const handleRevoke = async () => {
    if (editId == null) return;
    if (!window.confirm("승인을 철회할까요?\n승인란·검토란이 지워집니다."))
      return;
    try {
      setApproving(true);
      setError("");
      const saved = await revokeOfficialDocument(editId);
      applyDetail(saved);
      setApprovalMessage("승인이 철회되었습니다.");
      await loadList(searchKeyword, directionFilter);
    } catch (err: unknown) {
      reportError(setError, apiErrorMessage(err, "승인 철회에 실패했습니다."));
    } finally {
      setApproving(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (editId == null) return;
    try {
      setPdfDownloading(true);
      const name = documentNo?.trim() || title?.trim() || String(editId);
      await downloadOfficialDocumentPdf(editId, `공문서_${name}.pdf`);
    } catch (err) {
      notifyError(
        err instanceof Error
          ? err.message
          : "PDF 다운로드에 실패했습니다. Gotenberg 실행 여부를 확인해 주세요.",
      );
    } finally {
      setPdfDownloading(false);
    }
  };

  const handleUploadNow = async (files: FileList | null) => {
    if (!files?.length || attachmentsLocked) return;
    const selected = Array.from(files);
    if (fileRef.current) fileRef.current.value = "";
    if (editId == null) {
      setPendingFiles((prev) => [...prev, ...selected]);
      return;
    }
    try {
      setSaving(true);
      const uploaded = await uploadOfficialDocumentAttachments(
        editId,
        selected,
      );
      setAttachments((prev) => [...prev, ...uploaded]);
    } catch (err: unknown) {
      reportError(
        setError,
        apiErrorMessage(err, "첨부파일 업로드에 실패했습니다."),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAttachment = async (
    attachment: OfficialDocumentAttachment,
  ) => {
    if (editId == null || attachmentsLocked) return;
    if (!window.confirm(`"${attachment.fileName}" 첨부를 삭제할까요?`)) return;
    try {
      setSaving(true);
      await deleteOfficialDocumentAttachment(editId, attachment.attachmentId);
      setAttachments((prev) =>
        prev.filter((a) => a.attachmentId !== attachment.attachmentId),
      );
    } catch (err: unknown) {
      reportError(
        setError,
        apiErrorMessage(err, "첨부파일 삭제에 실패했습니다."),
      );
    } finally {
      setSaving(false);
    }
  };

  const renderDetailActions = () => (
    <div
      className="ca-toolbar-actions"
      style={{ display: "flex", gap: 8, flexWrap: "wrap" }}
    >
      {isApproved(status) ? (
        <button
          type="button"
          className="ncr-btn is-primary"
          disabled={pdfDownloading}
          onClick={() => void handleDownloadPdf()}
        >
          {pdfDownloading ? "PDF 생성 중…" : "PDF 다운로드"}
        </button>
      ) : null}
      {canRevoke ? (
        <button
          type="button"
          className="ncr-btn is-danger"
          disabled={saving || approving}
          onClick={() => void handleRevoke()}
        >
          {approving ? "철회 중..." : "승인 철회"}
        </button>
      ) : null}
      <ApprovalCancelButton
        detail={{
          approvalInProgress,
          pendingApprovalRequestId,
          canCancelApproval,
        }}
        className="ncr-btn is-secondary"
        disabled={saving || approving}
        onCancelled={async () => {
          if (editId == null) return;
          const detail = await fetchOfficialDocument(editId);
          applyDetail(detail);
          await loadList(searchKeyword, directionFilter);
        }}
      />
      {isDraft(status) && !approvalInProgress ? (
        <>
          <button
            type="button"
            className="ncr-btn is-secondary"
            onClick={openEditFromDetail}
          >
            수정
          </button>
          <button
            type="button"
            className="ncr-btn is-primary"
            disabled={saving || approving}
            onClick={() => {
              setApprovalMessage("");
              setApprovalOpen(true);
            }}
          >
            결재 요청
          </button>
          {canApproveDirect ? (
            <button
              type="button"
              className="ncr-btn is-approve"
              disabled={saving || approving}
              onClick={() => void handleApprove()}
            >
              {approving ? "승인 중..." : "승인"}
            </button>
          ) : null}
          <button
            type="button"
            className="ncr-btn is-danger"
            disabled={saving || approving}
            onClick={() => {
              if (editId == null) return;
              if (!window.confirm(`"${title}"를 삭제할까요?`)) return;
              void deleteOfficialDocument(editId).then(() => {
                backToList();
              });
            }}
          >
            삭제
          </button>
        </>
      ) : null}
      <button
        type="button"
        className="ncr-btn is-secondary"
        disabled={saving || approving}
        onClick={backToList}
      >
        목록
      </button>
    </div>
  );

  const renderEditActions = () => (
    <div className="quality-form__actions">
      <button
        type="submit"
        className="ncr-btn is-primary"
        disabled={saving || approving}
      >
        {saving ? "저장 중..." : "저장"}
      </button>
      <button
        type="button"
        className="ncr-btn is-secondary"
        disabled={saving || approving}
        onClick={() =>
          view.type === "edit" && editId != null
            ? setView({ type: "detail", officialDocumentId: editId })
            : backToList()
        }
      >
        {view.type === "edit" ? "상세" : "목록"}
      </button>
    </div>
  );

  if (showForm) {
    return (
      <main className="quality-shell sg-panel">
        <form
          className="quality-form"
          onSubmit={
            isEditing ? (e) => void handleSave(e) : (e) => e.preventDefault()
          }
        >
          {isDetail ? (
            <div
              className="quality-shell__toolbar"
              style={{ marginBottom: 12 }}
            >
              <div>
                <h3 style={{ margin: 0 }}>공문서 상세</h3>
                <p className="quality-form__hint" style={{ margin: "4px 0 0" }}>
                  {directionLabel(direction)} · {documentNo || "-"} ·{" "}
                  {title || "-"} ·{" "}
                  {withApprovalPendingLabel(
                    isApproved(status) ? "승인" : "작성중",
                    approvalInProgress,
                  )}
                </p>
                {approvalInProgress ? (
                  <p
                    className="quality-form__hint"
                    style={{ margin: "6px 0 0" }}
                  >
                    결재가 진행 중입니다. 내용을 바꾸려면 결재 요청을 취소한 뒤
                    수정하고 다시 요청하세요.
                  </p>
                ) : null}
              </div>
              {renderDetailActions()}
            </div>
          ) : (
            renderEditActions()
          )}

          {error ? <p className="quality-error">{error}</p> : null}
          {approvalMessage ? (
            <p className="quality-form__hint">{approvalMessage}</p>
          ) : null}

          <section className="quality-form__section">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 16,
                alignItems: "flex-start",
                marginBottom: 12,
              }}
            >
              <div>
                <h3 style={{ margin: 0 }}>
                  {view.type === "create"
                    ? "공문서 등록"
                    : view.type === "edit"
                      ? "공문서 수정"
                      : "공문서"}
                </h3>
                {isEditing ? (
                  <p
                    className="quality-form__hint"
                    style={{ margin: "4px 0 0" }}
                  >
                    저장하면 상세보기로 이동합니다.
                  </p>
                ) : null}
              </div>
              <StampBox
                authorName={authorName}
                reviewerName={reviewerName}
                approverName={approverName}
              />
            </div>
            <div className="quality-form__grid">
              <label>
                구분 *
                <select
                  value={direction}
                  onChange={(e) =>
                    setDirection(e.target.value as OfficialDocumentDirection)
                  }
                  disabled={locked}
                  required
                >
                  <option value="INCOMING">수신</option>
                  <option value="OUTGOING">발신</option>
                </select>
              </label>
              <label>
                문서번호
                <input
                  value={
                    documentNo ||
                    (view.type === "create" ? "저장 시 자동 채번" : "-")
                  }
                  readOnly
                />
              </label>
              <label>
                문서일
                <input
                  type="date"
                  value={documentDate}
                  onChange={(e) => setDocumentDate(e.target.value)}
                  readOnly={locked}
                />
              </label>
              <label className="is-span-2">
                제목 *
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="공문 제목"
                  maxLength={255}
                  required
                  readOnly={locked}
                />
              </label>
              <label className="is-span-2">
                상대 기관/부서
                <input
                  value={counterpart}
                  onChange={(e) => setCounterpart(e.target.value)}
                  placeholder="예: ○○시청 총무과"
                  maxLength={255}
                  readOnly={locked}
                />
              </label>
              <label className="is-span-2">
                내용
                <textarea
                  rows={8}
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="공문 내용"
                  readOnly={locked}
                />
              </label>
              <label className="is-span-2">
                비고
                <textarea
                  rows={3}
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                  placeholder="메모"
                  maxLength={2000}
                  readOnly={locked}
                />
              </label>
            </div>
          </section>

          <section className="quality-form__section">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                marginBottom: 8,
              }}
            >
              <h3 style={{ margin: 0 }}>첨부파일</h3>
              {!attachmentsLocked ? (
                <button
                  type="button"
                  className="ncr-btn is-secondary"
                  disabled={saving || approving}
                  onClick={() => fileRef.current?.click()}
                >
                  파일 추가
                </button>
              ) : null}
              <input
                ref={fileRef}
                type="file"
                multiple
                hidden
                onChange={(e) => void handleUploadNow(e.target.files)}
              />
            </div>
            <p className="quality-form__hint">
              관련 파일을 첨부할 수 있습니다. (최대 20개, 각 20MB)
              {editId == null ? " 신규는 저장 시 함께 업로드됩니다." : ""}
            </p>
            <ul className="edu-plan-files">
              {attachments.map((file) => (
                <li key={file.attachmentId}>
                  <div>
                    <strong>{file.fileName}</strong>
                    <span>{formatSize(file.fileSize)}</span>
                  </div>
                  <div className="edu-plan-files__actions">
                    <button
                      type="button"
                      className="ncr-btn is-secondary"
                      onClick={() =>
                        void downloadOfficialDocumentAttachment(
                          editId!,
                          file.attachmentId,
                          file.fileName,
                        ).catch(() =>
                          reportError(
                            setError,
                            "첨부파일 다운로드에 실패했습니다.",
                          ),
                        )
                      }
                    >
                      다운로드
                    </button>
                    {!attachmentsLocked ? (
                      <button
                        type="button"
                        className="ncr-btn is-danger"
                        disabled={saving || approving}
                        onClick={() => void handleDeleteAttachment(file)}
                      >
                        삭제
                      </button>
                    ) : null}
                  </div>
                </li>
              ))}
              {pendingFiles.map((file, idx) => (
                <li key={`pending-${file.name}-${idx}`}>
                  <div>
                    <strong>{file.name}</strong>
                    <span>저장 대기 · {formatSize(file.size)}</span>
                  </div>
                  <button
                    type="button"
                    className="ncr-btn is-danger"
                    onClick={() =>
                      setPendingFiles((prev) =>
                        prev.filter((_, i) => i !== idx),
                      )
                    }
                  >
                    제거
                  </button>
                </li>
              ))}
              {attachments.length === 0 && pendingFiles.length === 0 ? (
                <li className="is-empty">등록된 첨부파일이 없습니다.</li>
              ) : null}
            </ul>
          </section>

          {isDetail ? (
            <div className="quality-form__actions">{renderDetailActions()}</div>
          ) : (
            renderEditActions()
          )}

          {editId != null ? (
            <ApprovalRequestModal
              open={approvalOpen}
              titleDefault={`${title || "공문서"} 결재`}
              targetType={APPROVAL_TARGET_OFFICIAL_DOCUMENT}
              targetId={editId}
              onClose={() => setApprovalOpen(false)}
              onSubmitted={() => {
                setApprovalOpen(false);
                setApprovalMessage(
                  "결재 요청이 등록되었습니다. 헤더의 결재 탭에서 확인할 수 있습니다.",
                );
                void (async () => {
                  if (editId != null) {
                    try {
                      applyDetail(await fetchOfficialDocument(editId));
                    } catch {
                      /* ignore */
                    }
                  }
                  await loadList(searchKeyword, directionFilter);
                })();
              }}
            />
          ) : null}
        </form>
      </main>
    );
  }

  return (
    <main className="quality-shell sg-panel">
      <div className="quality-shell__toolbar">
        <div>
          <h2>{contractId != null ? "계약 공문서" : "공문서"}</h2>
          <p>수신·발신 공문을 한곳에서 관리합니다.</p>
        </div>
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <select
            value={directionFilter}
            onChange={(e) =>
              setDirectionFilter(e.target.value as DirectionFilter)
            }
            style={{
              height: 34,
              padding: "0 10px",
              border: "1px solid #cbd5e1",
              borderRadius: 8,
              fontSize: 13,
            }}
          >
            <option value="">전체</option>
            <option value="INCOMING">수신</option>
            <option value="OUTGOING">발신</option>
          </select>
          <input
            type="search"
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            placeholder="문서번호·제목·상대·내용 검색"
            style={{
              height: 34,
              padding: "0 12px",
              border: "1px solid #cbd5e1",
              borderRadius: 8,
              minWidth: 200,
              fontSize: 13,
            }}
          />
          <button
            type="button"
            className="ncr-btn is-primary"
            onClick={openCreate}
          >
            등록
          </button>
        </div>
      </div>

      {error ? <p className="quality-error">{error}</p> : null}

      <div className="quality-table-wrap">
        <table className="quality-table">
          <thead>
            <tr>
              <th style={{ width: 72 }}>구분</th>
              <th style={{ width: 160 }}>문서번호</th>
              <th>제목</th>
              <th style={{ width: 160 }}>상대</th>
              <th style={{ width: 110 }}>문서일</th>
              <th style={{ width: 72 }}>첨부</th>
              <th style={{ width: 88 }}>상태</th>
              <th style={{ width: 80 }}>관리</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8}>불러오는 중…</td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={8}>등록된 공문서가 없습니다.</td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={row.officialDocumentId}
                  onClick={() => void openDetail(row)}
                  style={{ cursor: "pointer" }}
                >
                  <td>
                    <span
                      className={`quality-badge ${
                        row.direction === "INCOMING"
                          ? "is-draft"
                          : "is-approved"
                      }`}
                    >
                      {directionLabel(row.direction)}
                    </span>
                  </td>
                  <td>{row.documentNo || "-"}</td>
                  <td>{row.title}</td>
                  <td>{row.counterpart || "-"}</td>
                  <td>{formatDate(row.documentDate)}</td>
                  <td>{row.attachmentCount ?? 0}</td>
                  <td>
                    <span
                      className={`quality-badge is-${withApprovalPendingClass(
                        (row.status || "draft").toLowerCase(),
                        row.approvalInProgress,
                      )}`}
                    >
                      {withApprovalPendingLabel(
                        isApproved(row.status) ? "승인" : "작성중",
                        row.approvalInProgress,
                      )}
                    </span>
                  </td>
                  <td>
                    {!isApproved(row.status) && !row.approvalInProgress ? (
                      <button
                        type="button"
                        className="ncr-btn is-danger"
                        onClick={(e) => void handleDeleteFromList(row, e)}
                      >
                        삭제
                      </button>
                    ) : (
                      "-"
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </main>
  );
}

export default OfficialDocumentPanel;
